import { Formik } from 'formik';
import React, { useImperativeHandle, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import { FormGroup, InlineNotification, Stack } from '@carbon/react';
import { OpeningHours, OpeningHoursField, toOpeningHoursPayload } from 'src/components/Branch/OpeningHoursField';
import { FormFieldUpload } from 'src/components/Form/FormFieldUpload';
import { FormikTextInput, FormikToggle } from 'src/components/Form/FormikInputs';
import MoneyInput from 'src/components/Form/MoneyInput';
import Separator from 'src/components/Separator/Separator';
import { useAuthPermissions } from 'src/hooks/useAuthPermissions';
import { RestaurantBranchEntity } from 'src/services/apis/types';

interface BranchFormProps {
  onSubmit: (values: any) => void;
  errorMessage: string | null;
  initialValues: RestaurantBranchEntity | null | undefined;
  onSelectedFile: ({ file, previewUrl }) => void;
}

const Schema = z.object({
  name: z.string().max(60).trim(),
  slug: z.string().max(24).trim(),
  address: z.string().max(255).trim(),
  latitude: z.preprocess((a) => parseFloat(`${a}`), z.number()),
  longitude: z.preprocess((a) => parseFloat(`${a}`), z.number()),
  bankAccountName: z.string().max(255).trim().optional(),
  bankAccountOwnerName: z.string().max(255).trim().optional(),
  bankAccountNumber: z.string().max(255).trim().optional(),
  bankAccountBranchName: z.string().max(255).trim().optional(),
});

const BranchForm = (props: BranchFormProps, ref) => {
  const { t } = useTranslation();
  const { isOwner } = useAuthPermissions();
  const formikRef = useRef<any>();

  useImperativeHandle(ref, () => {
    return {
      submitForm: formikRef.current?.submitForm,
      setFieldValue: formikRef.current?.setFieldValue,
      resetForm: () =>
        formikRef.current?.resetForm({
          values: {
            name: '',
            slug: '',
            address: '',
            photo: '',
            photoBlurhash: '',
            latitude: '',
            longitude: '',
            salesGoalPerDay: '',
            salesGoalPerWeek: '',
            salesGoalPerMonth: '',
            salesGoalPerYear: '',
            bankAccountName: '',
            bankAccountOwnerName: '',
            bankAccountNumber: '',
            bankAccountBranchName: '',
            zaloOrderingEnabled: false,
            openingHours: {},
          },
        }),
    };
  });

  return (
    <div>
      {props.errorMessage && <InlineNotification subtitle={props.errorMessage} />}
      <Formik
        innerRef={formikRef}
        enableReinitialize
        validationSchema={toFormikValidationSchema(Schema)}
        initialValues={{
          id: props.initialValues?.id,
          name: props.initialValues?.name,
          slug: props.initialValues?.slug,
          address: props.initialValues?.address,
          photo: props.initialValues?.photo,
          salesGoalPerDay: props.initialValues?.salesGoalPerDay || 0,
          salesGoalPerWeek: props.initialValues?.salesGoalPerWeek || 0,
          salesGoalPerMonth: props.initialValues?.salesGoalPerMonth || 0,
          salesGoalPerYear: props.initialValues?.salesGoalPerYear || 0,
          photoBlurhash: props.initialValues?.photoBlurhash,
          latitude: props.initialValues?.latitude ? `${props.initialValues?.latitude}` : null,
          longitude: props.initialValues?.longitude ? `${props.initialValues?.longitude}` : null,
          bankAccountName: props.initialValues?.bankAccountName ?? '',
          bankAccountOwnerName: props.initialValues?.bankAccountOwnerName ?? '',
          bankAccountNumber: props.initialValues?.bankAccountNumber ?? '',
          bankAccountBranchName: props.initialValues?.bankAccountBranchName ?? '',
          zaloOrderingEnabled: props.initialValues?.zaloOrderingEnabled ?? false,
          // The generated entity types the jsonb column loosely, so the week shape is narrowed
          // here — the same cast the Content screen does for `theme`.
          openingHours: (props.initialValues?.openingHours as OpeningHours) ?? {},
        }}
        onSubmit={(values) => props.onSubmit({ ...values, openingHours: toOpeningHoursPayload(values.openingHours) })}
      >
        {({ values, setFieldValue }) => (
          <FormGroup legendText={''}>
            <Stack gap={5}>
              <FormikTextInput name={'name'} label={t('common.name')} />
              <FormikTextInput name={'slug'} label={t('common.slug')} />
              <FormikTextInput name={'address'} label={t('common.address')} />
              <div style={{ display: 'flex', gap: '1rem' }}>
                <FormikTextInput name={'latitude'} label={t('common.latitude')} />
                <FormikTextInput name={'longitude'} label={t('common.longitude')} />
              </div>
              <Separator>Sales Goals</Separator>
              <MoneyInput name={'salesGoalPerDay'} label={t('common.salesGoalPerDay')} />
              <MoneyInput name={'salesGoalPerWeek'} label={t('common.salesGoalPerWeek')} />
              <MoneyInput name={'salesGoalPerMonth'} label={t('common.salesGoalPerMonth')} />
              <MoneyInput name={'salesGoalPerYear'} label={t('common.salesGoalPerYear')} />
              <Separator>Bank Account</Separator>
              <FormikTextInput name={'bankAccountName'} label={t('common.bankAccountName')} />
              <FormikTextInput name={'bankAccountOwnerName'} label={t('common.bankAccountOwnerName')} />
              <FormikTextInput name={'bankAccountNumber'} label={t('common.bankAccountNumber')} />
              <FormikTextInput name={'bankAccountBranchName'} label={t('common.bankAccountBranchName')} />
              <Separator>{t('branchHours.title')}</Separator>
              <OpeningHoursField
                value={values.openingHours ?? {}}
                onChange={(next) => setFieldValue('openingHours', next)}
              />
              <Separator>{t('common.integrations')}</Separator>
              <FormikToggle name={'zaloOrderingEnabled'} label={t('common.zaloOrderingEnabled')} disabled={!isOwner} />
              <FormFieldUpload
                title={t('common.branchPhoto')}
                description={t('messages.photoRecommended', { size: 1, aspectRatio: '16x9' })}
                cropperAspectRatio={16 / 9}
                aspectRatio={'16x9'}
                onSelectedFile={props.onSelectedFile}
                value={values.photo}
                onChange={(data) => {
                  setFieldValue('photo', data.fileId);
                  setFieldValue('photoBlurhash', data.blurhash);
                }}
              />
            </Stack>
          </FormGroup>
        )}
      </Formik>
    </div>
  );
};

export default React.forwardRef(BranchForm);
